import React, { forwardRef, isValidElement } from "react";
import "../../styles/colorSystem.css"
import { useCopy } from "../../hooks/useCopy";
import { useToast } from "../../hooks/useToast";
import copyIcon from "../icons/System/copy.svg"
import { postToApp } from "../../bridges/appBridge";

/* ----------------------------- 내부 유틸 ----------------------------- */
const sizeMap = {
  md: "h-[48px] py-[12px] text-b1 gap-[4px]",
  sm: "h-[36px] py-[12px] text-b1 gap-[4px]",
};

function cx(...cls) {
  return cls.filter(Boolean).join(" ");
}

// 문자열/객체도 아이콘으로 렌더링
function renderAdornment(ad, defaultSize = "w-4 h-4") {
  if (!ad) return null;
  if (isValidElement(ad)) return ad;          // <svg/>, <span/>, 등
  if (typeof ad === "string") {               // "/path/to/icon.png"
    return <img src={ad} alt="" aria-hidden className={`${defaultSize} object-contain`} />;
  }
  // { src, alt?, className? } 형태도 지원
  if (typeof ad === "object" && ad.src) {
    return (
      <img
        src={ad.src}
        alt={ad.alt ?? ""}
        className={cx(defaultSize, "object-contain", ad.className)}
        aria-hidden={ad.alt ? undefined : true}
      />
    );
  }
  return null;
}


function withIconSize(ad, sizeClass = "w-6 h-6") {
  if (!ad) return null;

  // <svg />, <span/> 등 React 엘리먼트면 className을 합쳐서 강제
  if (isValidElement(ad)) {
    const prev = ad.props?.className || "";
    return React.cloneElement(ad, { className: cx(prev, sizeClass) });
  }

  // "경로 문자열"이면 object 형태로 바꿔서 className 주입
  if (typeof ad === "string") {
    return { src: ad, className: sizeClass };
  }

  // {src, className} 형태면 className만 합쳐서 강제
  if (typeof ad === "object" && ad.src) {
    return { ...ad, className: cx(ad.className, sizeClass) };
  }

  return ad;
}


/* 공통 베이스 */
export const ButtonBase = forwardRef(function ButtonBase(
  { className, size = "md", left, right, children, disabled, loading, ...rest },
  ref
) {
  const isOff = disabled || loading;
  return (

    <button
      ref={ref}
      disabled={isOff}
      className={cx(
        "inline-flex w-full items-center justify-center rounded-[16px] transition-colors gap-1",
        sizeMap[size],
        isOff ? "cursor-not-allowed" : "cursor-pointer",
        className,
        isOff && "!bg-btn-disabled-fill !text-text-disabled !border-0"
      )}
      {...rest}
    >
      {left && <span className="shrink-0">{renderAdornment(left)}</span>}
      <span className="truncate">{loading ? "로딩…" : children}</span>
      {right && <span className="shrink-0">{renderAdornment(right)}</span>}
    </button>

  );
});

/* 단일 버튼: type으로 스타일 선택 */
const styles = {
  fill: "bg-btn-primary-fill text-text-inverse",
  border: "bg-surface-light-white text-text-primary border border-btn-secondary-border",
  gray: "bg-btn-disabled-fill text-text-disabled",
  empty: "bg-bg-transparent text-text-tertiary",
  alert: "bg-btn-alert-fill text-text-danger"
};

// 버튼 그룹을 감싸는 공통 래퍼
export function ButtonFrame({ children, className }) {
  return (
    <div className={cx("bg-surface-light-white p-[16px]", className)}>
      {children}
    </div>
  );
}


/* -------------------------- 1) 기본 버튼 : 타입으로 변경 가능-------------------------- */
export const Button = React.forwardRef(function Button(
  { type = "fill", className, disabled, loading, left, right, ...rest },
  ref
) {
  const variant = styles[type] || styles.fill;
  const forceDisabled = type === "disabled";
  return (
    <ButtonBase
      ref={ref}
      className={`${variant} ${className || ""}`}
      disabled={forceDisabled || disabled}
      loading={loading}
      left={left}
      right={right}
      {...rest}
    />
  );
});


export function SingleButton(props) {
  return (
    <ButtonFrame>
      <Button {...props} />
    </ButtonFrame>
  );
}

/* 필요하면 기존 이름으로도 alias 제공 */
export const FillButton = forwardRef(function FillButton(p, r) {
  return (
    <ButtonFrame>
      <Button ref={r} type="fill" {...p} />
    </ButtonFrame>
  );
});

export const BorderButton = forwardRef(function BorderButton(p, r) {
  return (
    <ButtonFrame>
      <Button ref={r} type="border" {...p} />
    </ButtonFrame>
  );
});

export const GrayButton = forwardRef(function GrayButton(p, r) {
  return (
    <ButtonFrame>
      <Button ref={r} type="gray" {...p} />
    </ButtonFrame>
  );
});



/* ------------------------------ 4) 배경/테두리 없는 얇은 버튼 ------------------------------ */
export const EmptyButton = React.forwardRef(function EmptyButton(
  { type = "empty", left, right, className, ...rest },
  ref
) {
  const variant = styles[type] || styles.fill;

  return (
    <ButtonFrame>
      <ButtonBase
        ref={ref}
        size="sm"
        type="empty"
        left={left}
        right={right}
        className={cx(variant, className)}
        {...rest}
      />
    </ButtonFrame>
  );
});

/* --------------------------- 5) 얇은 버튼 --------------------------- */
export const ThinButton = React.forwardRef(function ThinButton(
  { type = "fill", left, right, className, disabled, loading, ...rest },
  ref
) {
  const variant = styles[type] || styles.fill;
  const forceDisabled = type === "disabled";

  return (
    <ButtonFrame>
      <ButtonBase
        ref={ref}
        size="sm"
        left={left}
        right={right}
        className={cx(variant, className)}
        disabled={forceDisabled || disabled}
        loading={loading}
        {...rest}
      />
    </ButtonFrame>
  );
});


/* --------------------- 6) 세로 2버튼 -------------------- */
export function TwinColumn({
  topText,
  bottomText,
  onTop,
  onBottom,
  topType,
  bottomType,
  topLeftIcon,
  topRightIcon,
  bottomLeftIcon,
  bottomRightIcon,
  topDisabled,
  bottomDisabled,
}) {
  const bottomIsEmpty = bottomType === "empty";

  return (
    <ButtonFrame>
      <div className="flex flex-col gap-[8px]">
        <Button
          type={topType || "fill"}
          onClick={onTop}
          left={topLeftIcon}
          right={topRightIcon}
          disabled={topDisabled}
        >
          {topText}
        </Button>
        <Button
          type={bottomType || "border"}
          size={bottomIsEmpty ? "sm" : "md"}
          onClick={onBottom}
          left={bottomLeftIcon}
          right={bottomRightIcon}
          disabled={bottomDisabled}
        >
          {bottomText}
        </Button>
      </div>
    </ButtonFrame>
  );
}

/* ---------- 7) 좌:회색 고스트 / 우:프라이머리 (가로 2버튼 조합) ---------- */
export function SplitRowHalf({
  leftText, rightText, onLeft, onRight,
  leftType, rightType,
  leftLeftIcon, leftRightIcon, rightLeftIcon, rightRightIcon,
  leftDisabled, rightDisabled,
}) {
  return (
    <ButtonFrame>
      <div className="grid grid-cols-2 w-full gap-[12px]">
        <Button
          type={leftType || "gray"}
          onClick={onLeft}
          left={withIconSize(leftLeftIcon)}
          right={withIconSize(leftRightIcon)}
          disabled={leftDisabled}
        >
          {leftText}
        </Button>

        <Button
          type={rightType || "fill"}
          onClick={onRight}
          left={withIconSize(rightLeftIcon)}
          right={withIconSize(rightRightIcon)}
          disabled={rightDisabled}
        >
          {rightText}
        </Button>
      </div>
    </ButtonFrame>
  );
}




/* ---------- 8) 좌:회색 고스트 / 우:프라이머리 (가로 2버튼 조합) ---------- */
export function SplitRowQuarter({
  leftText,
  rightText,
  onLeft,
  onRight,
  leftType,
  rightType,
  leftLeftIcon,
  leftRightIcon,
  rightLeftIcon,
  rightRightIcon,
  leftDisabled,
  rightDisabled,
}) {
  return (
    <ButtonFrame>
      <div className="grid grid-cols-4 w-full gap-[12px]">
        <Button
          type={leftType || "empty"}
          className="col-span-1"
          onClick={onLeft}
          left={leftLeftIcon}
          right={leftRightIcon}
          disabled={leftDisabled}
        >
          {leftText}
        </Button>
        <Button
          type={rightType || "fill"}
          className="col-span-3"
          onClick={onRight}
          left={rightLeftIcon}
          right={rightRightIcon}
          disabled={rightDisabled}
        >
          {rightText}
        </Button>
      </div>
    </ButtonFrame>
  );
}


/* ---------- 9) 패딩영역 없는 심플 버튼 ---------- */
const SIMPLE_CLASS = {
  select: "border border-border-primary text-text-primary text-t4",
  unselect: "border border-border-subtle text-text-disabled text-b1",
};

export const SimpleButton = ({
  type = "unselect",
  label = "label",
  className = "",
  ...props
}) => {
  const base =
    "inline-flex items-center justify-center h-[48px] px-[16px] py-[12px] rounded-[12px] w-full bg-surface-light-white"
  const color = SIMPLE_CLASS[type] ?? SIMPLE_CLASS.unselect;

  return (
    <button
      type="button"
      className={`${base} ${color} ${className}`}
      {...props}
    >
      {label}
    </button>
  );
};
// export const SimpleButton = forwardRef(function SimpleButton(
//   { className, left, right, disabled = false, children, ...rest },
//   ref
// ) {
//   const isOff = disabled;

//   return (
//     <button
//       ref={ref}
//       disabled={isOff}
//       className={cx(
//         "inline-flex items-center justify-center gap-[4px]",
//         "h-[48px] px-[16px] py-[12px]",
//         "rounded-[12px] w-full",
//         "bg-surface-light-white",
//         isOff
//           ? "border border-border-subtle text-text-disabled text-b1 cursor-not-allowed"
//           : "border border-border-primary text-text-primary text-t4 cursor-pointer",
//         className
//       )}
//       {...rest}
//     >
//       {left && <span className="shrink-0">{renderAdornment(left)}</span>}
//       <span className="truncate">
//         {children}
//       </span>
//       {right && <span className="shrink-0">{renderAdornment(right)}</span>}
//     </button>
//   );
// });





/* ---------- 10) 복사 버튼 ---------- */
export const CopyButton = ({
  value,
  className = "",
  disabled: disabledProp = false,
  toastPlacement = "bottom",
  toastTopOffset,
  toastBottomOffset,
}) => {
  const { copy, isCopying } = useCopy();
  const { showToast } = useToast();

  const handleClick = async () => {
    if (!value) return;

    const toastOptions =
      toastPlacement === "bottom"
        ? { placement: "bottom", bottomOffset: toastBottomOffset }
        : { placement: "top", topOffset: toastTopOffset };

    // ✅ 1) RN WebView 안인 경우: 네이티브로 복사만 요청 (브라우저 clipboard X)
    if (typeof window !== "undefined" && window.ReactNativeWebView) {
      try {
        postToApp("COPY_TO_CLIPBOARD", { text: value });
        showToast("텍스트를 복사했습니다", toastOptions);
      } catch (e) {
        console.error("RN copy postMessage error:", e);
      }
      return;
    }

    // ✅ 2) 일반 웹 브라우저: 기존 훅 사용 (navigator.clipboard 등)
    const ok = await copy(value);

    if (ok) {
      showToast("텍스트를 복사했습니다", toastOptions);
    }
  };


  const disabled = disabledProp || isCopying;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center text-c2 text-text-subtler gap-[4px] ${className}`}
    >
      <img src={copyIcon} alt="복사하기" className="w-5 h-5" />
      복사하기
    </button>
  );
};


/* ---------- 11) 예약 수정/삭제 버튼 ---------- */
const VARIANT_CLASS = {
  blue: "bg-btn-minor-fill text-text-primary",
  red: "bg-btn-alert-fill text-text-danger",
};

export const ReserveButton = ({
  type = "red",
  label = "수정",
  className = "",
  ...props
}) => {
  const base =
    "inline-flex items-center justify-center text-c1 rounded-[16px] px-[12px] py-[7px] h-[32px] w-full";
  const color = VARIANT_CLASS[type] ?? VARIANT_CLASS.blue;

  return (
    <button
      type="button"
      className={`${base} ${color} ${className}`}
      {...props}
    >
      {label}
    </button>
  );
};